import { Injectable, Signal, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SwUpdate } from '@angular/service-worker';

/**
 * Update state of the Angular service worker, exposed as signals so any shell or
 * page can render a "downloading the latest version…" affordance.
 *
 * The service worker (see `ngsw-config.json` + `provideServiceWorker` in
 * app.module.ts) keeps a complete versioned copy of the app in the Cache API and
 * serves the app from it, swapping versions atomically. That is how the Electron
 * desktop shell — which remote-loads https://connect.{domain} rather than
 * bundling the app — ends up running from a local copy, and it is also what makes
 * the browser PWA installable/offline-capable.
 *
 * This is deliberately inert unless the worker is actually registered:
 * `SwUpdate` is only provided by `provideServiceWorker`, so it is injected
 * `optional` (specs and any consumer without those providers get `null`), and
 * every method short-circuits when `SwUpdate.isEnabled` is false — `ng serve`,
 * the staging build, private-mode/no-SW browsers. Nothing here throws and
 * nothing here reloads in that case.
 */
@Injectable({ providedIn: 'root' })
export class AppVersionService {

  /**
   * True only when an Angular service worker is registered for this page and the
   * app can talk to it. Callers should treat `false` as "there is no update
   * mechanism here" — that is the signal AppComponent uses to decide whether the
   * kit's /version.json poll (`<crwd-app-update>` / `CrwdAppUpdateService`) is
   * still needed.
   */
  readonly isEnabled: boolean;

  /** A newer version was detected and its files are being fetched into the cache. */
  readonly downloading: Signal<boolean>;

  /** A newer version is fully cached and one `activateUpdate()` away. */
  readonly ready: Signal<boolean>;

  /** A newer version exists: either still downloading, or downloaded and ready. */
  readonly updateAvailable: Signal<boolean>;

  private readonly swUpdate = inject(SwUpdate, { optional: true });
  private readonly downloadingState = signal(false);
  private readonly readyState = signal(false);
  private activating = false;
  private checkTimer: ReturnType<typeof setInterval> | null = null;

  constructor() {
    this.isEnabled = this.swUpdate?.isEnabled ?? false;
    this.downloading = this.downloadingState.asReadonly();
    this.ready = this.readyState.asReadonly();
    this.updateAvailable = computed(() => this.downloadingState() || this.readyState());

    if (!this.swUpdate || !this.isEnabled) {
      return;
    }

    this.swUpdate.versionUpdates
      .pipe(takeUntilDestroyed())
      .subscribe((event) => {
        switch (event.type) {
          case 'VERSION_DETECTED':
            this.downloadingState.set(true);
            break;
          case 'VERSION_READY':
            this.downloadingState.set(false);
            this.readyState.set(true);
            break;
          case 'VERSION_INSTALLATION_FAILED':
          case 'NO_NEW_VERSION_DETECTED':
            this.downloadingState.set(false);
            break;
        }
      });

    // The cached version is broken beyond repair (the browser evicted part of it
    // and the server no longer has those files). Only a full reload, which lets
    // the worker install the current server version from scratch, recovers.
    this.swUpdate.unrecoverable
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.reloadOnce());
  }

  /**
   * Keep asking the worker for a newer version while the tab is open: every
   * `intervalMs` and whenever the tab becomes visible again. Without this the
   * worker only looks on navigation — exactly the moment the update is applied
   * anyway — so a parked production tab never learned that a deploy had
   * happened and nothing could offer it a refresh (Vincent, 2026-09-19: "in
   * prod, I no longer see when a new version is available"; the staging build
   * has no worker and shows the kit's /version.json pill). Idempotent, inert
   * without a worker.
   */
  startUpdateChecks(intervalMs = 2 * 60_000): void {
    if (!this.swUpdate?.isEnabled || this.checkTimer) {
      return;
    }
    this.checkTimer = setInterval(() => void this.checkForUpdate(), intervalMs);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') void this.checkForUpdate();
    });
  }

  /**
   * Asks the worker to compare its manifest against the server's and, if a new
   * version exists, download it. Resolves `true` when a new version is ready.
   * Resolves `false` — never rejects — when there is nothing new or no worker.
   */
  async checkForUpdate(): Promise<boolean> {
    if (!this.swUpdate?.isEnabled) {
      return false;
    }
    try {
      return await this.swUpdate.checkForUpdate();
    } catch {
      // Offline, or the worker is mid-install. Nothing to report.
      return false;
    }
  }

  /**
   * Swaps this tab onto the cached new version and reloads so every chunk the
   * page later imports comes from that same version. No-op (and, critically, no
   * reload) when there is no worker.
   */
  async activateUpdate(): Promise<boolean> {
    if (!this.swUpdate?.isEnabled || this.activating) {
      return false;
    }
    this.activating = true;
    try {
      await this.swUpdate.activateUpdate();
    } catch {
      // Activation failed (e.g. nothing was ready). Reload anyway: the worker
      // serves whatever version it has and the page ends up consistent.
    }
    this.reloadPage();
    return true;
  }

  /**
   * Single seam for `location.reload()` so specs can assert on it without jsdom
   * complaining about navigation.
   */
  protected reloadPage(): void {
    location.reload();
  }

  /**
   * Reload at most once per short window, so a genuinely broken deploy cannot
   * trap the user in a reload loop.
   */
  private reloadOnce(): void {
    const KEY = 'crewdle:sw-unrecoverable-reload-at';
    try {
      const last = Number(sessionStorage.getItem(KEY) ?? '0');
      if (Date.now() - last < 30_000) {
        return;
      }
      sessionStorage.setItem(KEY, String(Date.now()));
    } catch {
      // sessionStorage unavailable (private mode/quota) — fall through and reload.
    }
    this.reloadPage();
  }
}
